import { useEffect, useRef, useState } from 'react';

import { CONTACT_ATTRIBUTES } from '@cloud-wave/neon-common-lib/app';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import { useLocalStorage } from 'lib/common/hooks/useLocalStorage';

import { LogEvents, logger } from 'lib/common/components/LoggerController';
import Text from 'lib/common/components/Text';

import CONNECTION_TYPES from 'lib/common/constants/connectionType';
import CONTACT_STATES from 'lib/common/constants/contactStates';
import { LOCAL_STORAGE_KEYS } from 'lib/common/constants/localStorageKeys';

import TTask from 'lib/common/types/Task';
import getContactAttributes from 'lib/common/utils/getContactAttributes';
import getUserName from 'lib/common/utils/getUserName';
import isVoice from 'lib/common/utils/isVoice';
import setContactAttributes from 'lib/common/utils/setContactAttributes';

import { usePermissionsContext } from '../PermissionsContext';
import Context, { TWaitForRelatedCallsConfirmation } from './Context';

function RelatedCallsProvider({ children }: { children: React.ReactNode }) {
  const { fetch_ } = useAuthContext();
  const { config } = useConfigContext();
  const { hasPermission } = usePermissionsContext();
  const { getStorageItem, setStorageItem } = useLocalStorage();

  // Fetch related calls state from local storage, if available to persist across page reloads
  const [relatedId, setRelatedId] = useState(getStorageItem(LOCAL_STORAGE_KEYS.RELATED_CALLS_ID) || '');
  const [isRelatingCall, setIsRelatingCall] = useState(true);
  const [lastCallWith, setLastCallWith] = useState(
    getStorageItem(LOCAL_STORAGE_KEYS.RELATED_CALLS_LAST_CALL_WITH) || ''
  );

  // Refs to hold the latest values for use in ContactContext which has a stale reference of the handleRelatedCalls function
  const relatedIdRef = useRef(relatedId);
  const isRelatingCallRef = useRef(isRelatingCall);
  const numberDialedRef = useRef('');

  const hasRelatedCallsPermission = hasPermission({ type: 'tenant', permission: 'RELATED_CALLS' });
  const canRelateCall = Boolean(relatedId && hasRelatedCallsPermission);

  // Persist related calls state to local storage on change
  // Set refs to latest values on change for use in ContactContext
  useEffect(() => {
    relatedIdRef.current = relatedId;
    setStorageItem(LOCAL_STORAGE_KEYS.RELATED_CALLS_ID, relatedId);
  }, [relatedId]);

  useEffect(() => {
    isRelatingCallRef.current = isRelatingCall;
  }, [isRelatingCall]);

  useEffect(() => {
    setStorageItem(LOCAL_STORAGE_KEYS.RELATED_CALLS_LAST_CALL_WITH, lastCallWith);
  }, [lastCallWith]);

  const resetRelatedCalls = () => {
    setRelatedId('');
    setIsRelatingCall(true);
    setLastCallWith('');
    numberDialedRef.current = '';
  };

  const handleConnectedCall = ({ task }: { task: TTask }) => {
    const newRelatedId =
      getContactAttributes({ task }, CONTACT_ATTRIBUTES.RELATED_CALLS)?.[CONTACT_ATTRIBUTES.RELATED_CALLS] || '';

    setRelatedId(newRelatedId);
    setIsRelatingCall(true);
    setLastCallWith(
      getUserName({
        ...task.profile,
        fallback: task.connectionValue,
      })
    );
    numberDialedRef.current = '';
  };

  const handleConnectingCall = ({ task }: { task: TTask }) => {
    setContactAttributes({
      contactId: task.contact.contactId,
      attributes: [{ key: CONTACT_ATTRIBUTES.RELATED_CALLS, value: relatedIdRef.current }],
      fetch_,
      agentServiceUrl: config.AGENT_SERVICE_V2_URL,
    });
  };

  const handleRelatedCalls = ({
    changes,
    task,
  }: {
    changes: { status: keyof typeof CONTACT_STATES; eventTimestamp?: string };
    task: TTask;
  }) => {
    if (!hasRelatedCallsPermission || !isVoice(task)) {
      return;
    }

    const isInboundConnectedCall =
      changes.status === 'CONNECTED' && changes.eventTimestamp && task.connectionType === CONNECTION_TYPES.INBOUND;

    const isConnectingOutboundCallWithRelatedIdAndMatchingNumber =
      changes.status === 'CONNECTING' &&
      task.connectionType === CONNECTION_TYPES.OUTBOUND &&
      relatedIdRef.current &&
      isRelatingCallRef.current &&
      task.connectionValue === numberDialedRef.current;

    if (!isInboundConnectedCall && !isConnectingOutboundCallWithRelatedIdAndMatchingNumber) {
      return;
    }

    if (isInboundConnectedCall) {
      return handleConnectedCall({ task });
    }

    return handleConnectingCall({ task });
  };

  const waitForRelatedCallsConfirmation = async ({
    dialedNumber,
    openModal,
    closeModal,
    isDialpadTransfer,
    isSoftphone,
  }: TWaitForRelatedCallsConfirmation): Promise<boolean> => {
    if (!canRelateCall || !isSoftphone || isDialpadTransfer) {
      // Related calls not available, allow call to proceed
      return true;
    }

    if (isRelatingCall) {
      // User has opted in to related calls, allow call to proceed
      numberDialedRef.current = dialedNumber;
      return true;
    }

    // User has opted out of related calls, confirm they want to proceed
    return new Promise((resolve) =>
      openModal({
        title: 'Are You Sure?',
        content: (
          <Text>
            This call will not be linked to your last call with <Text.Bold>{lastCallWith}</Text.Bold>, and a log entry
            will note that it was left unlinked.
          </Text>
        ),
        primaryButtonText: 'Continue',
        onSave: () => {
          logger.info(LogEvents.OUTBOUND_CALL.RELATED_CALLS_UNLINKED, {
            dialedNumber,
            lastCallWith,
            relatedId: relatedIdRef.current,
          });
          resetRelatedCalls();
          closeModal();
          resolve(true);
        },
        onSecondary: () => {
          setIsRelatingCall(true);
          resolve(false);
          closeModal();
        },
        onClose: () => {
          resolve(false);
          closeModal();
        },
      })
    );
  };

  return (
    <Context.Provider
      value={{
        state: {
          relatedId,
          isRelatingCall,
          lastCallWith,
          canRelateCall,
        },
        actions: {
          setIsRelatingCall,
          resetRelatedCalls,
          handleRelatedCalls,
          waitForRelatedCallsConfirmation,
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}

export default RelatedCallsProvider;
